import { SEO } from "@/components/SEO";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Phone, ArrowRight, ExternalLink, MessageSquarePlus } from "lucide-react";
import { BUSINESS } from "@/data/businessInfo";
import { ServiceCardsSection, LocationCardsSection } from "@/components/SharedSections";
import axios from "axios";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } };

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    axios.get(`${API}/reviews`).then(r => setReviews(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <SEO title="Electrician Reviews Port St. Lucie FL - 5.0 Stars on Google | Southern Light Solutions" description="Read real 5-star Google reviews from Southern Light Solutions customers across the Treasure Coast. 15+ reviews. Licensed electrician in Port St. Lucie, FL. BBB A-rated." path="/reviews" />
      <section className="bg-[#FAFAFA] py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#BF8D30] mb-3 block" style={{ fontFamily: 'Outfit, sans-serif' }}>Customer Reviews</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#401303] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>What Our Customers Say</h1>
            <div className="flex items-center justify-center gap-4 mt-5">
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map(i => <Star key={i} className="w-6 h-6 fill-[#D97904] text-[#D97904]" />)}
              </div>
              <span className="text-2xl font-bold text-[#401303]" style={{ fontFamily: 'Outfit, sans-serif' }}>5.0</span>
            </div>
            <p className="mt-2 text-sm text-[#6B3A1F]" style={{ fontFamily: 'Manrope, sans-serif' }}>Based on {reviews.length || 25} reviews from Google</p>
            <div className="flex flex-wrap justify-center gap-3 mt-5">
              <a href={BUSINESS.social.google} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-[#6B3A1F]/15 rounded-full text-sm font-semibold text-[#401303] hover:border-[#D97904] transition-colors duration-200" data-testid="gbp-link">
                <GoogleIcon /> View on Google <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <a href={BUSINESS.social.googleReview} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 btn-gradient rounded-full text-sm" data-testid="leave-review-btn">
                <MessageSquarePlus className="w-4 h-4" /> Leave a Review
              </a>
            </div>
          </div>

          {/* Stats bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {[
              { label: "Google Rating", value: "5.0", sub: `${reviews.length || 25} reviews` },
              { label: "Facebook", value: "5.0", sub: "7 reviews" },
              { label: "Yelp", value: "5.0", sub: "Verified" },
              { label: "BBB Rating", value: "A", sub: "Accredited" },
            ].map((stat, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#6B3A1F]/10 p-5 text-center">
                <p className="text-2xl font-bold text-[#D97904]" style={{ fontFamily: 'Outfit, sans-serif' }}>{stat.value}</p>
                <p className="text-sm font-semibold text-[#401303] mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{stat.label}</p>
                <p className="text-xs text-[#6B3A1F]" style={{ fontFamily: 'Manrope, sans-serif' }}>{stat.sub}</p>
              </div>
            ))}
          </div>

          {/* Review grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="reviews-grid">
            {reviews.map((review, i) => (
              <motion.div key={review.id || i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.3, delay: i * 0.04 }} className="bg-white rounded-2xl border border-[#6B3A1F]/10 p-6 hover:shadow-lg transition-shadow duration-300 warm-glow" data-testid={`review-${i}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-1">
                    {Array.from({ length: review.rating }, (_, j) => <Star key={j} className="w-4 h-4 fill-[#D97904] text-[#D97904]" />)}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-[#6B3A1F]/60">
                    <GoogleIcon />
                    <span>Google</span>
                  </div>
                </div>
                <p className="text-sm text-[#401303] leading-relaxed mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>"{review.text}"</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#BF3604] to-[#D97904] flex items-center justify-center text-white text-xs font-bold">{review.name?.[0]}</div>
                    <div>
                      <p className="text-sm font-semibold text-[#401303]" style={{ fontFamily: 'Outfit, sans-serif' }}>{review.name}</p>
                      <p className="text-xs text-[#6B3A1F]">{review.city}, FL</p>
                    </div>
                  </div>
                  {review.service && <span className="text-xs px-2.5 py-1 bg-[#D97904]/10 rounded-full text-[#6B3A1F] font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>{review.service}</span>}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Leave a Review CTA */}
          <div className="mt-14 bg-white rounded-2xl border border-[#6B3A1F]/10 p-8 lg:p-10 text-center">
            <div className="flex items-center justify-center gap-1 mb-4">
              {[1,2,3,4,5].map(i => <Star key={i} className="w-8 h-8 fill-[#D97904] text-[#D97904]" />)}
            </div>
            <h2 className="text-2xl font-bold text-[#401303] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Had a Great Experience?</h2>
            <p className="text-[#6B3A1F] mb-6 max-w-md mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>Your feedback helps other homeowners and businesses find a trusted electrician on the Treasure Coast. We'd love to hear about your experience.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <a href={BUSINESS.social.googleReview} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-7 py-3.5 btn-gradient rounded-full text-sm" data-testid="leave-review-cta">
                <GoogleIcon /> Leave a Google Review
              </a>
              <a href={BUSINESS.social.facebook} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-7 py-3.5 border-2 border-[#401303] text-[#401303] font-bold rounded-full hover:bg-[#401303] hover:text-white transition-colors duration-200 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Review on Facebook
              </a>
            </div>
          </div>
        </div>
      </section>

      <ServiceCardsSection />
      <LocationCardsSection />

      {/* CTA */}
      <section className="bg-warm-gradient py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Experience the Difference</h2>
          <p className="text-white/80 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Join our growing list of satisfied customers on the Treasure Coast.</p>
          <Link to="/book" className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-[#401303] font-bold rounded-full text-sm hover:bg-white/90 transition-colors duration-200" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="reviews-book-btn">
            Book Service <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
